import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useApi } from "@pcs/telemetry-api";
import type {
  TelemetrySummary,
  SearchTelemetryRequest,
  SearchTelemetryResponse,
} from "@/api/telemetry";
import type { TelemetrySortFieldType } from "@/components/telemetry/search/telemetrySortField";
import type { PageSize } from "@/components/telemetry/common/PaginationFooter";

type HeaderTypeValue = "request" | "response";

const DEFAULT_PAGE_SIZE: PageSize = 25;

/** Parse MM/dd/yyyy (or MM-dd-yyyy) into an ISO 8601 string, or return the input as-is. */
function toIsoDate(value: string): string {
  const match = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return value;
  const [, mm, dd, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
}

interface UseTelemetrySearchParams {
  pageNumber: number;
  pageSize: PageSize;
  customerId: string;
  method: string;
  path: string;
  statusCode: string;
  apiName: string;
  fromTimestamp: string;
  toTimestamp: string;
  headerName: string;
  headerValue: string;
  headerType: HeaderTypeValue;
  sortField: TelemetrySortFieldType;
  isSortDescending: boolean;
}

interface UseTelemetrySearchResult {
  items: TelemetrySummary[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function useTelemetrySearch({
  pageNumber,
  pageSize,
  customerId,
  method,
  path,
  statusCode,
  apiName,
  fromTimestamp,
  toTimestamp,
  headerName,
  headerValue,
  headerType,
  sortField,
  isSortDescending,
}: UseTelemetrySearchParams): UseTelemetrySearchResult {
  const { api } = useApi();
  const [items, setItems] = useState<TelemetrySummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    setRefetchCounter((prev) => prev + 1);
  }, []);

  useEffect(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const request: SearchTelemetryRequest = {
      customerId: customerId ? Number(customerId) : null,
      method: method || null,
      path: path || null,
      statusCode: statusCode ? Number(statusCode) : null,
      apiName: apiName || null,
      fromTimestamp: fromTimestamp ? toIsoDate(fromTimestamp) : null,
      toTimestamp: toTimestamp ? toIsoDate(toTimestamp) : null,
      headerName: headerName || null,
      headerValue: headerValue || null,
      headerType: headerName ? headerType : null,
      pageNumber,
      pageSize,
      sortField,
      isSortDescending,
    };

    setIsLoading(true);
    setError(null);

    api.api
      .v1TelemetrySearchPost(request, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          const data = response.data as SearchTelemetryResponse;
          setItems(data.items ?? []);
          setTotalCount(data.totalCount ?? 0);
        }
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) {
          if (isAxiosError(err)) {
            setError(
              err.response?.data?.title ?? "Failed to search telemetry",
            );
          } else {
            setError("An unexpected error occurred");
          }
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [
    api,
    pageNumber,
    pageSize,
    customerId,
    method,
    path,
    statusCode,
    apiName,
    fromTimestamp,
    toTimestamp,
    headerName,
    headerValue,
    headerType,
    sortField,
    isSortDescending,
    refetchCounter,
  ]);

  return { items, totalCount, isLoading, error, refetch };
}

export default useTelemetrySearch;
export { DEFAULT_PAGE_SIZE };
export type { HeaderTypeValue };
